<template>
  <div class="w-full min-h-screen bg-[#f4f5f3]">
    <Toast />
    <div class="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
      <section class="relative overflow-hidden bg-[#2f7f57] text-white flex items-center justify-center px-8 py-12">
        <div class="absolute left-8 top-20 text-[120px] opacity-10 select-none">🌹</div>
        <div class="absolute right-10 bottom-16 text-[120px] opacity-10 select-none">🌸</div>

        <div class="text-center">
          <div class="text-[68px] leading-none mb-4 opacity-95">❀</div>
          <h1 class="text-5xl md:text-6xl font-semibold tracking-tight">Bueno</h1>
          <p class="text-xl md:text-2xl text-white/85 mt-3">Система управления цветочным магазином</p>
        </div>
      </section>

      <section class="flex items-center justify-center px-6 py-10 md:px-10">
        <div class="w-full max-w-[520px]">
          <h2 class="text-4xl md:text-5xl font-semibold text-gray-900">Вход в систему</h2>
          <p class="text-xl text-gray-500 mt-2 mb-8">Введите данные для входа</p>

          <form class="space-y-5" @submit.prevent="handleLogin">
            <div>
              <label for="login-id" class="block text-lg text-gray-800 mb-2">
                {{ loginMode === 'admin' ? 'Email' : 'Штрихкод' }}
              </label>
              <input
                id="login-id"
                v-model.trim="identifier"
                :type="loginMode === 'admin' ? 'email' : 'text'"
                :placeholder="loginMode === 'admin' ? 'admin@florashop.ru' : 'Введите штрихкод сотрудника'"
                :autocomplete="loginMode === 'admin' ? 'username' : 'off'"
                class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-lg text-gray-800 outline-none focus:border-[#2f7f57]"
              >
            </div>

            <div>
              <label for="password" class="block text-lg text-gray-800 mb-2">Пароль</label>
              <div class="relative">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="••••••••"
                  autocomplete="current-password"
                  class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pr-12 text-lg text-gray-800 outline-none focus:border-[#2f7f57]"
                >
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-gray-500 transition-colors hover:text-[#2f7f57]"
                  :aria-label="showPassword ? 'Скрыть пароль' : 'Показать пароль'"
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" class="cursor-pointer"/>
                </button>
              </div>
            </div>

            <button
              type="submit"
              :disabled="isLoading"
              class="w-full rounded-xl bg-[#2f7f57] text-white py-3 text-xl font-semibold hover:bg-[#286f4c] transition-colors cursor-pointer"
            >
              {{ isLoading ? 'Вход...' : 'Войти' }}
            </button>
          </form>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import { login } from './../store/auth'

const router = useRouter()
const toast = useToast()
const loginMode = ref<'admin' | 'employee'>('admin')
const identifier = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)

const handleLogin = async () => {
  if (!identifier.value || !password.value) {
    toast.add({
      severity: 'warn',
      summary: 'Проверьте данные',
      detail: 'Заполните логин и пароль',
      life: 2500
    })
    return
  }

  isLoading.value = true
  try {
    const data = await login(identifier.value, password.value)
    const role = String(data?.role || '')
    const userName = String(data?.user?.name || '')

    if (loginMode.value === 'admin' && role !== 'admin') {
      toast.add({
        severity: 'error',
        summary: 'Доступ запрещен',
        detail: 'Этот аккаунт не является администратором',
        life: 3000
      })
      return
    }

    if (loginMode.value === 'employee' && role === 'admin') {
      toast.add({
        severity: 'error',
        summary: 'Неверный режим входа',
        detail: 'Для администратора выберите режим "Администратор"',
        life: 3000
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: `Добро пожаловать${userName ? ', ' + userName : ''}`,
      life: 2200
    })

    if (role === 'admin') {
      router.push({ name: 'Account' })
    } else {
      router.push({ name: 'Sale' })
    }
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка входа',
      detail: e?.response?.data?.message || 'Не удалось войти в систему',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}
</script>
